import { mostrarAcesso } from "@/components/AccessShare";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { impersonate } from "@/components/ImpersonationBanner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GraduationCap, UserPlus, Loader2, Copy, Link2, Trash2, CheckCircle2, Pencil, Eye } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { avatarUrl } from "@/components/UserAvatar";

import { confirmar } from "@/components/ConfirmDialog";
const PROFILES = [
  { v: "professor", l: "Professor(a)" },
  { v: "instrutor", l: "Instrutor(a)" },
  { v: "mentor", l: "Mentor(a)" },
  { v: "representante", l: "Representante Credenciado" },
];

export default function Instructors() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", profile: "professor" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/instructors").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Preencha nome e e-mail.");
    setSaving(true);
    try {
      const res = await api.post("/instructors", form);
      setResult(res.data);
      setForm({ name: "", email: "", profile: "professor" });
      load();
      toast.success(res.data.email_sent ? "Convite enviado por e-mail!" : "Professor cadastrado. Copie o link abaixo.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copiado!"); };
  const resend = async (i) => {
    try {
      const res = await api.post(`/instructors/${i.id}/resend-invite`);
      mostrarAcesso(res.data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const activate = async (i) => {
    if (!(await confirmar(`Ativar ${i.name} agora? A conta fica ativa e as turmas dela já ficam ligadas. Nenhum e-mail é enviado; quando ela for entrar, use "Enviar acesso".`))) return;
    try { await api.post(`/instructors/${i.id}/activate`); toast.success("Professora ativada."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (i) => {
    if (!(await confirmar(`Remover ${i.name}?`))) return;
    await api.delete(`/instructors/${i.id}`); load(); toast.success("Professor removido.");
  };

  const label = (p) => PROFILES.find((x) => x.v === p)?.l || "Professor(a)";

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Professores</h1>
          <p className="text-slate-500 mt-1">Cadastre professores, instrutores e mentores.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-create-instructor" className="text-white font-semibold" style={{ background: "#1E9E2A" }}>
              <UserPlus className="h-4 w-4 mr-1.5" /> Novo professor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Cadastrar professor</DialogTitle>
              <DialogDescription>Ele receberá um convite por e-mail para criar a senha de acesso.</DialogDescription>
            </DialogHeader>
            {result ? (
              <div className="space-y-4 py-2">
                <div className="rounded-lg bg-teal-50 border border-teal-100 p-4 text-center">
                  <CheckCircle2 className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-700 font-medium">{result.name} cadastrado(a)!</p>
                  <p className="text-xs text-slate-500 mt-1">{result.email_sent ? "Convite enviado por e-mail." : "E-mail não enviado — use o link."}</p>
                </div>
                <div className="flex gap-2">
                  <Input readOnly value={result.invite_link} className="text-xs" />
                  <Button variant="outline" onClick={() => copy(result.invite_link)}><Copy className="h-4 w-4" /></Button>
                </div>
                <Button className="w-full" variant="outline" onClick={() => setResult(null)}>Cadastrar outro</Button>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-2">
                  <div className="space-y-1.5">
                    <Label>Nome completo *</Label>
                    <Input data-testid="instructor-name-input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>E-mail *</Label>
                    <Input data-testid="instructor-email-input" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Perfil</Label>
                    <Select value={form.profile} onValueChange={(v) => setForm((p) => ({ ...p, profile: v }))}>
                      <SelectTrigger data-testid="instructor-profile-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PROFILES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="instructor-submit-button" onClick={create} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar convite"}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {list === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : list.length === 0 ? (
        <div className="p-12 text-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50">
          <GraduationCap className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nenhum professor cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((i) => (
            <div key={i.id} data-testid={`instructor-card-${i.id}`} onClick={() => navigate(`/admin/professores/${i.id}`)} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-slate-300 transition">
              <div className="flex items-start justify-between">
                {i.avatar ? (
                  <img src={avatarUrl(i.avatar)} alt={i.name} className="h-11 w-11 rounded-full object-cover ring-2 ring-[#232D4F]" />
                ) : (
                  <div className="h-11 w-11 rounded-full grid place-items-center text-white font-bold" style={{ background: "#232D4F" }}>
                    {i.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${i.status === "cadastrado" ? "bg-teal-50 text-teal-700" : "bg-orange-50 text-orange-700"}`}>
                  {i.status === "cadastrado" ? "Ativo" : i.status === "primeiro_acesso" ? "Aguardando 1º acesso" : "Pendente"}
                </span>
              </div>
              <button onClick={() => navigate(`/admin/professores/${i.id}`)} className="mt-3 font-display font-semibold text-slate-800 hover:text-[#1E9E2A] text-left">{i.name}</button>
              <div className="text-xs text-slate-400">{i.email}</div>
              <div className="text-xs text-[#1E9E2A] font-semibold mt-1">{label(i.profile)}</div>
              {i.turmas_resumo && (
                <div className="mt-2 flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5 text-xs text-slate-600" data-testid={`instructor-alunas-${i.id}`}>
                  <span><b className="text-[15px] text-[#232D4F]">{i.turmas_resumo.alunas.toLocaleString("pt-BR")}</b> alunas</span>
                  <span><b className="text-[15px] text-[#1E9E2A]">{i.turmas_resumo.ativas.toLocaleString("pt-BR")}</b> ativas</span>
                  <span className="ml-auto text-slate-400">{i.turmas_resumo.turmas} {i.turmas_resumo.turmas === 1 ? "turma" : "turmas"}</span>
                </div>
              )}
              <div className="mt-2" data-testid={`instructor-states-${i.id}`}>
                <div className="text-[11px] text-slate-400 mb-1">Estados de atuação</div>
                {(i.territory_states || []).length === 0 ? (
                  <span className="text-xs text-slate-400">Nenhum estado definido</span>
                ) : (i.territory_states || []).length >= 27 ? (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-[#1E9E2A] text-white">Todo o Brasil</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(i.territory_states || []).map((u) => <span key={u} className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-green-50 text-green-800 border border-green-200">{u}</span>)}
                  </div>
                )}
              </div>
              {(i.permissions || []).includes("cadastro") && (
                <span className="inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5">Cadastra alunos</span>
              )}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                {i.status !== "cadastrado" && (
                  <button onClick={() => activate(i)} className="text-xs text-teal-700 hover:text-teal-800 font-semibold flex items-center gap-1" data-testid={`activate-instructor-${i.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Ativar agora
                  </button>
                )}
                {(i.status !== "cadastrado" || i.onboarding_required) && (
                  <button data-testid={`resend-instructor-${i.id}`} onClick={() => resend(i)} className="text-xs text-slate-500 hover:text-[#232D4F] flex items-center gap-1">
                    <Link2 className="h-3.5 w-3.5" /> Enviar acesso
                  </button>
                )}
                <button data-testid={`edit-instructor-${i.id}`} onClick={() => navigate(`/admin/professores/${i.id}`)} className="text-xs text-slate-500 hover:text-[#232D4F] flex items-center gap-1">
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
                {i.status === "cadastrado" && (
                  <button onClick={() => (i.student_id ? impersonate("aluno", i.student_id, setUser, navigate) : impersonate("professor", i.id, setUser, navigate))}
                    title={i.student_id ? "Entrar como aluno (lista de cursos)" : "Entrar como professor"}
                    className="text-xs text-amber-700 hover:text-amber-800 flex items-center gap-1" data-testid={`impersonate-instructor-${i.id}`}>
                    <Eye className="h-3.5 w-3.5" /> {i.student_id ? "Acessar como aluno" : "Acessar"}
                  </button>
                )}
                <button onClick={() => remove(i)} className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 ml-auto">
                  <Trash2 className="h-3.5 w-3.5" /> Remover
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
